import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function ChangePasswordPage() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const forced = user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (next !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Contraseña actualizada correctamente.");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md p-8">
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-slate-900 text-white mb-5">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {forced ? "Cambiá tu contraseña" : "Cambiar contraseña"}
        </h1>
        <p className="text-sm text-slate-500 mt-2 mb-6">
          {forced
            ? "Es tu primer ingreso. Por seguridad, definí una nueva contraseña para continuar."
            : "Actualizá la contraseña de tu cuenta."}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-2">
            <Label htmlFor="current">Contraseña actual</Label>
            <Input
              id="current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="next">Nueva contraseña</Label>
            <Input
              id="next"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Repetir nueva contraseña</Label>
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              data-testid="confirm-password-input"
            />
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="change-password-error">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading} data-testid="change-password-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar contraseña"}
          </Button>
          {forced && (
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-2"
              data-testid="change-password-logout"
            >
              Cerrar sesión
            </button>
          )}
        </form>
      </Card>
    </div>
  );
}
